import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Info, Trophy } from "lucide-react";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import Tooltip from "./Tooltip";

const points = (value) => Number(value || 0).toFixed(1);
const signedPoints = (value) => {
    const numericValue = Number(value || 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(1)}`;
};
const percent = (value) => `${Math.round(Number(value || 0))}%`;

// Positive values are good for the owner, negative values are bad. Zero and
// unknown values stay neutral so the colour only ever carries a real signal.
const signClass = (value) => {
    const numericValue = Number(value || 0);
    if (numericValue > 0) return "text-green";
    if (numericValue < 0) return "text-coral";
    return "text-text-muted";
};

const formatDate = (value) => {
    if (!value) return "Trade date unavailable";
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(`${value}T00:00:00Z`));
};

const outcomeClass = (result) =>
    result === "W" ? "border-l-green" : result === "L" ? "border-l-coral" : "border-l-mustard";

const COLUMN_HELP = {
    netImpact:
        "Net impact: Estimated starter points this owner gained or lost from their trades. Compares what the players they acquired actually scored as starters against the best lineup they could have fielded without the trade.",
    starts:
        "Starts: Total weekly lineup starts by players acquired in the owner’s trades since the trade date.",
    starterRate:
        "Starter rate: Share of eligible weeks in which acquired players were started rather than benched.",
    resultsChanged:
        "Results changed: Number of this owner’s weekly matchups whose win, loss, or tie outcome would have been different without the trade.",
    netWins:
        "Net wins: Wins this owner gained or lost because of their trades. A win counts as 1, a tie as 0.5, and a loss as 0, and the pre-trade lineup estimate is subtracted from the actual result. Each week counts once, even if several trades affected it.",
};

const SIDE_HELP =
    "Each side is scored on its own: the starter points and wins that owner gained or lost compared with the lineup they could have fielded without the trade. The side that has gained more so far is marked as the current winner.";

const HeaderWithInfo = ({ label, help }) => (
    <span className="inline-flex items-center justify-end gap-1">
        {label}
        <Tooltip label={help} wrapperClassName="inline-flex">
            <span
                className="inline-flex cursor-help text-text-muted/70 transition-colors hover:text-forest focus:text-forest focus:outline-none"
                tabIndex={0}
                role="img"
                aria-label={`About ${label}`}
            >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
        </Tooltip>
    </span>
);

const naturalList = (items) => {
    if (items.length < 2) return items[0] || "";
    if (items.length === 2) return items.join(" and ");
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const playerPointList = (players = []) =>
    naturalList(
        players.map((player) => `${player.name} (${points(player.points)} pts)`)
    );

// Work out which side of a trade is ahead. Net impact (starter points) is the
// primary measure; net wins breaks ties. Returns null when the sides are level
// or the analysis has not run yet.
const tradeLeader = (trade, pending) => {
    if (pending) return null;
    const receivers = trade.receivers || [];
    if (receivers.length < 2) return null;
    const ranked = [...receivers].sort((a, b) => {
        const impactGap = Number(b.estimated_net_lineup_impact || 0) - Number(a.estimated_net_lineup_impact || 0);
        if (impactGap !== 0) return impactGap;
        return Number(b.net_wins || 0) - Number(a.net_wins || 0);
    });
    const [first, second] = ranked;
    const impactGap = Number(first.estimated_net_lineup_impact || 0) - Number(second.estimated_net_lineup_impact || 0);
    const winsGap = Number(first.net_wins || 0) - Number(second.net_wins || 0);
    if (impactGap === 0 && winsGap === 0) return null;
    return { team_id: first.team_id, owner: first.owner, impactGap, winsGap };
};

const TradeHub = ({ data, pending = false }) => {
    const trades = useMemo(
        () => (Array.isArray(data?.trades) ? data.trades : []),
        [data]
    );
    const owners = Array.isArray(data?.owners) ? data.owners : [];
    const ownersWithTrades = owners.filter((owner) => Number(owner.trades) > 0);
    const [ownerFilter, setOwnerFilter] = useState("all");
    const visibleTrades = useMemo(
        () =>
            ownerFilter === "all"
                ? trades
                : trades.filter((trade) =>
                      trade.teams.some((team) => String(team.team_id) === ownerFilter)
                  ),
        [ownerFilter, trades]
    );
    const [selectedTradeId, setSelectedTradeId] = useState(trades[0]?.id || null);
    const [showAllWeeks, setShowAllWeeks] = useState(false);
    const tradeListRef = useRef(null);

    useEffect(() => {
        if (!visibleTrades.some((trade) => trade.id === selectedTradeId)) {
            setSelectedTradeId(visibleTrades[0]?.id || null);
        }
    }, [selectedTradeId, visibleTrades]);

    const selectedTrade = useMemo(
        () => visibleTrades.find((trade) => trade.id === selectedTradeId) || visibleTrades[0],
        [selectedTradeId, visibleTrades]
    );

    useLayoutEffect(() => {
        const list = tradeListRef.current;
        const activeTrade = list?.querySelector('[aria-current="true"]');
        if (!list || !activeTrade) return;

        const listRect = list.getBoundingClientRect();
        const activeRect = activeTrade.getBoundingClientRect();
        if (activeRect.top < listRect.top) {
            list.scrollTop -= listRect.top - activeRect.top;
        } else if (activeRect.bottom > listRect.bottom) {
            list.scrollTop += activeRect.bottom - listRect.bottom;
        }
    }, [selectedTradeId]);

    if (!trades.length) {
        return (
            <div className="rounded-card border border-border-card bg-white px-6 py-12 text-center shadow-sm">
                <h3 className="font-display text-xl font-bold text-forest">No completed trades found</h3>
                <p className="mt-2 text-sm font-medium text-text-muted">
                    This league has no completed trades for the selected season.
                </p>
            </div>
        );
    }

    const impactGames = selectedTrade?.impact_games || [];
    const displayedGames = showAllWeeks ? impactGames : impactGames.slice(0, 5);
    // While the lineup analysis is still running, impact metrics are unknown.
    // Show a pulsing skeleton bar in place of each value so the columns read
    // as "loading" rather than empty.
    const pendingMark = (
        <span
            aria-label="Loading"
            role="status"
            className="inline-block h-3.5 w-12 max-w-full animate-pulse rounded-full bg-forest/10 align-middle"
        />
    );
    const metric = (formatted) => (pending ? pendingMark : formatted);
    const selectedLeader = selectedTrade ? tradeLeader(selectedTrade, pending) : null;

    return (
        <CopyableContainer
            title={<LeagueTabHeading eyebrow="Trade Hub" title="Trade impact timeline" />}
            fileName="trade-hub"
            preserveTableLayout
        >
            <div className="space-y-6">
                <p className="max-w-3xl text-sm font-semibold text-text-muted">
                    Follow every accepted trade in the league from owner-level results into each acquired player’s starts, starter points, and the matchups those starts could have influenced. Every number is scored from the point of view of the owner it sits next to.
                </p>

                <section className="overflow-hidden rounded-card border border-border-card bg-white shadow-sm">
                    <div className="border-b border-border-card bg-forest px-4 py-3 text-xs font-black uppercase tracking-[0.08em] text-white">
                        Owner trade impact
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-[820px] w-full text-left text-sm">
                            <thead className="bg-surface text-xs font-black uppercase tracking-[0.07em] text-text-muted">
                                <tr>
                                    <th className="px-4 py-3">Owner / team</th>
                                    <th className="px-3 py-3 text-center">Deals</th>
                                    <th className="px-3 py-3 text-right"><HeaderWithInfo label="Net impact" help={COLUMN_HELP.netImpact} /></th>
                                    <th className="px-3 py-3 text-right"><HeaderWithInfo label="Starts" help={COLUMN_HELP.starts} /></th>
                                    <th className="px-3 py-3 text-right"><HeaderWithInfo label="Starter rate" help={COLUMN_HELP.starterRate} /></th>
                                    <th className="px-3 py-3 text-right"><HeaderWithInfo label="Results changed" help={COLUMN_HELP.resultsChanged} /></th>
                                    <th className="px-4 py-3 text-right"><HeaderWithInfo label="Net wins" help={COLUMN_HELP.netWins} /></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#e6e2d9]">
                                {ownersWithTrades.map((owner) => (
                                    <tr key={owner.team_id} className="font-bold text-text">
                                        <td className="px-4 py-3">
                                            <strong className="block text-forest">{owner.owner}</strong>
                                            <span className="text-[0.68rem] text-text-muted">{owner.team}</span>
                                        </td>
                                        <td className="px-3 py-3 text-center font-mono">{owner.trades}</td>
                                        <td className={`px-3 py-3 text-right font-mono ${pending ? "" : signClass(owner.estimated_net_lineup_impact)}`}>{metric(signedPoints(owner.estimated_net_lineup_impact))}</td>
                                        <td className="px-3 py-3 text-right font-mono">{metric(owner.starts)}</td>
                                        <td className="px-3 py-3 text-right font-mono">{metric(percent(owner.starter_rate))}</td>
                                        <td className="px-3 py-3 text-right font-mono">{metric(owner.potential_matchups)}</td>
                                        <td className={`px-4 py-3 text-right font-mono ${pending ? "" : signClass(owner.net_wins)}`}>{metric(signedPoints(owner.net_wins))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="grid gap-5 lg:grid-cols-[minmax(230px,.7fr)_minmax(0,1.55fr)]">
                    <div className="relative lg:min-h-0">
                    <aside className="rounded-card border border-border-card bg-white p-4 shadow-sm lg:absolute lg:inset-0 lg:flex lg:min-h-0 lg:flex-col">
                        <h3 className="font-display text-xl font-bold text-forest">Deals</h3>
                        <p className="mt-1 text-xs font-semibold text-text-muted">Choose a trade to inspect its post-trade footprint.</p>
                        {ownersWithTrades.length > 1 && (
                            <label className="mt-3 block text-[0.62rem] font-black uppercase tracking-[0.07em] text-text-muted">
                                Show trades involving
                                <select
                                    value={ownerFilter}
                                    onChange={(event) => { setOwnerFilter(event.target.value); setShowAllWeeks(false); }}
                                    className="mt-1 block w-full rounded-lg border border-border-card bg-surface px-2.5 py-2 text-xs font-bold normal-case tracking-normal text-forest focus:border-mustard focus:outline-none"
                                >
                                    <option value="all">All owners ({trades.length} {trades.length === 1 ? "trade" : "trades"})</option>
                                    {ownersWithTrades.map((owner) => (
                                        <option key={owner.team_id} value={String(owner.team_id)}>
                                            {owner.owner} ({owner.trades})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <div
                            ref={tradeListRef}
                            className="mt-3 max-h-[28rem] space-y-2 overflow-y-auto overscroll-contain pr-1 lg:max-h-none lg:min-h-0 lg:flex-1"
                            role="region"
                            aria-label="Trade list"
                            tabIndex={0}
                        >
                            {visibleTrades.map((trade) => {
                                const ownersLabel = trade.teams.map((team) => team.owner).join(" ↔ ");
                                const active = trade.id === selectedTrade?.id;
                                const leader = tradeLeader(trade, pending);
                                return (
                                    <button
                                        type="button"
                                        key={trade.id}
                                        aria-current={active ? "true" : undefined}
                                        onClick={() => { setSelectedTradeId(trade.id); setShowAllWeeks(false); }}
                                        className={`w-full rounded-lg border p-3 text-left transition-colors ${active ? "border-mustard bg-mustard-light" : "border-transparent hover:border-border-card hover:bg-surface"}`}
                                    >
                                        <span className="block text-xs font-black text-forest">{ownersLabel}</span>
                                        <span className="mt-1 block text-[0.68rem] font-semibold text-text-muted">Week {trade.week} · {formatDate(trade.date)}</span>
                                        {pending ? (
                                            <span className="mt-1 flex items-center gap-2 text-[0.68rem] font-bold text-text-muted">Scoring impact {pendingMark}</span>
                                        ) : leader ? (
                                            <span className="mt-1 flex items-center gap-1 text-[0.68rem] font-bold text-green">
                                                <Trophy className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                                                <span>{leader.owner} ahead by <span className="font-mono">{points(leader.impactGap)}</span> pts</span>
                                            </span>
                                        ) : (
                                            <span className="mt-1 block text-[0.68rem] font-bold text-text-muted">Even so far</span>
                                        )}
                                        {!pending && (
                                            <span className="mt-1 block font-mono text-[0.62rem] font-semibold text-text-muted">
                                                {trade.receivers.map((receiver, index) => (
                                                    <React.Fragment key={receiver.team_id}>
                                                        {index > 0 && " · "}
                                                        {receiver.owner.split(" ")[0]} <span className={signClass(receiver.estimated_net_lineup_impact)}>{signedPoints(receiver.estimated_net_lineup_impact)}</span>
                                                    </React.Fragment>
                                                ))}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </aside>
                    </div>

                    {selectedTrade && (
                        <article className="rounded-card border border-border-card bg-white p-4 shadow-sm sm:p-5">
                            <header className="border-b border-border pb-4">
                                <p className="text-[0.67rem] font-black uppercase tracking-[0.09em] text-text-muted">Week {selectedTrade.week} · {formatDate(selectedTrade.date)}</p>
                                <h3 className="mt-1 font-display text-2xl font-bold text-forest">
                                    {selectedTrade.teams.map((team) => team.owner).join(" ↔ ")}
                                </h3>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    {pending ? (
                                        <span className="rounded-full bg-surface px-3 py-1 font-mono text-xs font-bold text-text-muted">Scoring trade impact…</span>
                                    ) : selectedLeader ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-light px-3 py-1 text-xs font-black text-green">
                                            <Trophy className="h-3.5 w-3.5" aria-hidden="true" />
                                            {selectedLeader.owner} is winning this trade so far
                                        </span>
                                    ) : (
                                        <span className="rounded-full bg-surface px-3 py-1 text-xs font-black text-text-muted">This trade is even so far</span>
                                    )}
                                    <Tooltip label={SIDE_HELP} wrapperClassName="inline-flex">
                                        <span
                                            className="inline-flex cursor-help text-text-muted/70 transition-colors hover:text-forest focus:text-forest focus:outline-none"
                                            tabIndex={0}
                                            role="img"
                                            aria-label="How trade sides are scored"
                                        >
                                            <Info className="h-3.5 w-3.5" aria-hidden="true" />
                                        </span>
                                    </Tooltip>
                                </div>
                            </header>

                            <div className="mt-5 grid gap-5 md:grid-cols-2">
                                {selectedTrade.receivers.map((receiver) => {
                                    const isLeader = selectedLeader?.team_id === receiver.team_id;
                                    return (
                                        <section
                                            key={receiver.team_id}
                                            className={`rounded-xl border p-3 ${isLeader ? "border-green/40 bg-green-light/40" : "border-border-card bg-white"}`}
                                        >
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div>
                                                    <h4 className="font-display text-lg font-bold text-forest">{receiver.owner} received</h4>
                                                    <p className="text-xs font-semibold text-text-muted">{receiver.team}</p>
                                                </div>
                                                {isLeader && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-wide text-white">
                                                        <Trophy className="h-3 w-3" aria-hidden="true" /> Winning
                                                    </span>
                                                )}
                                            </div>
                                            <dl className="mt-2 mb-3 grid grid-cols-2 gap-2">
                                                <div className="rounded-lg bg-surface px-3 py-2">
                                                    <dt className="text-[0.58rem] font-black uppercase tracking-[0.06em] text-text-muted">{receiver.owner.split(" ")[0]}’s net impact</dt>
                                                    <dd className={`font-mono text-sm font-bold ${pending ? "text-text-muted" : signClass(receiver.estimated_net_lineup_impact)}`}>
                                                        {metric(`${signedPoints(receiver.estimated_net_lineup_impact)} pts`)}
                                                    </dd>
                                                </div>
                                                <div className="rounded-lg bg-surface px-3 py-2">
                                                    <dt className="text-[0.58rem] font-black uppercase tracking-[0.06em] text-text-muted">{receiver.owner.split(" ")[0]}’s net wins</dt>
                                                    <dd className={`font-mono text-sm font-bold ${pending ? "text-text-muted" : signClass(receiver.net_wins)}`}>
                                                        {metric(signedPoints(receiver.net_wins))}
                                                    </dd>
                                                </div>
                                            </dl>
                                            {receiver.players.length > 0 && (
                                                <div className="overflow-hidden rounded-lg border border-border-card bg-white">
                                                    <div className="grid grid-cols-[minmax(0,1fr)_48px_72px] gap-2 bg-surface px-3 py-2 text-[0.58rem] font-black uppercase tracking-[0.06em] text-text-muted">
                                                        <span>Player</span><span className="text-right">Starts</span><span className="text-right">Starter pts</span>
                                                    </div>
                                                    {receiver.players.map((player) => (
                                                        <div key={player.player_id} className="grid grid-cols-[minmax(0,1fr)_48px_72px] gap-2 border-t border-[#e6e2d9] px-3 py-2 text-xs font-bold">
                                                            <span><strong className="block text-forest">{player.name}</strong></span>
                                                            <span className="text-right font-mono">{pending ? pendingMark : `${player.starts}/${player.eligible_games}`}</span>
                                                            <span className="text-right font-mono text-forest">{metric(points(player.starter_points))}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {receiver.draft_picks?.length > 0 && (
                                                <div className={`${receiver.players.length ? "mt-2" : ""} overflow-hidden rounded-lg border border-border-card bg-white`}>
                                                    <div className="bg-surface px-3 py-2 text-[0.58rem] font-black uppercase tracking-[0.06em] text-text-muted">Draft picks</div>
                                                    {receiver.draft_picks.map((pick) => (
                                                        <div key={pick.asset_id} className="border-t border-[#e6e2d9] px-3 py-2 text-xs font-bold text-forest">
                                                            {pick.season} Round {pick.round} pick
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </section>
                                    );
                                })}
                            </div>

                            <section className="mt-6">
                                <div className="flex flex-wrap items-end justify-between gap-3">
                                    <div>
                                        <h4 className="font-display text-xl font-bold text-forest">Result-changing matchups</h4>
                                        <p className="mt-1 text-xs font-semibold text-text-muted">Projection-selected counterfactual lineups changed the result in these games.</p>
                                    </div>
                                    {!pending && impactGames.length > 5 && (
                                        <button type="button" onClick={() => setShowAllWeeks((value) => !value)} className="rounded border border-border bg-surface px-2.5 py-1.5 text-[0.65rem] font-black uppercase tracking-wide text-forest hover:bg-bg">
                                            {showAllWeeks ? "Show fewer" : `Show all ${impactGames.length}`}
                                        </button>
                                    )}
                                </div>
                                {pending ? (
                                    <p className="mt-3 rounded-lg border border-border-card bg-surface px-3 py-4 text-xs font-semibold text-text-muted">
                                        Lineup analysis is still running — result-changing matchups will appear here when it finishes.
                                    </p>
                                ) : displayedGames.length ? (
                                    <div className="mt-3 space-y-2">
                                        {displayedGames.map((game) => {
                                            const startedIds = new Set(
                                                (game.started_players || []).map((player) => player.player_id)
                                            );
                                            const replacementIds = new Set(
                                                (game.counterfactual_players || []).map((player) => player.player_id)
                                            );
                                            const changedStarters = (game.started_players || []).filter(
                                                (player) => !replacementIds.has(player.player_id)
                                            );
                                            const changedReplacements = (game.counterfactual_players || []).filter(
                                                (player) => !startedIds.has(player.player_id)
                                            );

                                            return (
                                                <div key={`${game.team_id}-${game.week}`} className={`border-l-4 bg-surface px-3 py-2 ${outcomeClass(game.result)}`}>
                                                    <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs font-bold">
                                                        <strong className="text-forest">Week {game.week} · {game.owner} {game.result} {points(game.team_score)}–{points(game.opponent_score)}</strong>
                                                        <span className="font-mono text-text-muted">
                                                            <span className={signClass(game.net_lineup_impact)}>{signedPoints(game.net_lineup_impact)}</span> pts for {game.owner.split(" ")[0]} · <span className={signClass(game.net_wins)}>{signedPoints(game.net_wins)}</span> wins · {points(game.margin)} margin
                                                        </span>
                                                    </div>
                                                    <p className="mt-1 text-[0.7rem] font-semibold text-text-muted">
                                                        {game.owner} started {playerPointList(changedStarters)} against {game.opponent_owner}. Without this trade, {game.owner} might have started {playerPointList(changedReplacements)} across the affected {naturalList(game.affected_slots || [])} slots.
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="mt-3 rounded-lg border border-border-card bg-surface px-3 py-4 text-xs font-semibold text-text-muted">No projection-selected counterfactual changed a matchup result for this trade.</p>
                                )}
                            </section>
                        </article>
                    )}
                </section>
            </div>
        </CopyableContainer>
    );
};

export default TradeHub;
